import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { FormSelect } from "./form-fields/FormSelect";
import { NumberInput } from "./form-fields/NumberInput";
import { TemplateSelector } from "./form-fields/TemplateSelector";
import { formSchema, FormValues } from "../types";

interface CreateLaunchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateLaunchDialog({ open, onOpenChange }: CreateLaunchDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      form_mapping_id: undefined,
      id_cliente: undefined,
      template_v2_id: undefined,
      max_respuestas: 1,
      report_lifetime: 30,
    },
  });

  useEffect(() => {
  }, [form.formState.errors]);

  // V2: Query jotform_form_mappings instead of forms
  const { data: formMappings } = useQuery({
    queryKey: ["jotform_form_mappings", currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("jotform_form_mappings")
        .select("id, jotform_form_id, form_title")
        .eq('project_id', projectClient.getProjectId())
        .order("form_title");

      if (error) throw error;
      return data;
    },
  });

  const { data: clients } = useQuery({
    queryKey: ["clients", currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("clientes")
        .select("id, nombre")
        .eq('project_id', projectClient.getProjectId())
        .order("nombre");

      if (error) throw error;
      return data;
    },
  });

  // V2: Query report_templates_v2 instead of report_templates
  const { data: templates } = useQuery({
    queryKey: ["templates_v2", currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("report_templates_v2")
        .select("id, name")
        .eq('project_id', projectClient.getProjectId())
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const generateThreeDigitCode = () => {
    // Genera un código de tres dígitos (entre 100 y 999)
    return Math.floor(Math.random() * 900 + 100).toString();
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      // Convert report_lifetime to days
      const reportLifetimeInMs = values.report_lifetime * 24 * 60 * 60 * 1000;

      // V2: Insert with form_mapping_id and template_v2_id
      const { data: launchData, error: launchError } = await projectClient
        .from("form_launches")
        .insert({
          form_mapping_id: values.form_mapping_id,
          id_cliente: values.id_cliente,
          template_v2_id: values.template_v2_id,
          max_respuestas: values.max_respuestas,
          report_lifetime: reportLifetimeInMs,
          generacion_reporte: values.generacion_reporte || 'individual_equipo',
          report_type: values.report_type || 'individual_equipo',
          email_sending_type: values.email_sending_type || 'tras_generacion',
          project_id: projectClient.getProjectId()
        })
        .select("id")
        .single();

      if (launchError) throw launchError;

      toast.success("Lanzamiento V2 creado correctamente");
      queryClient.invalidateQueries({ queryKey: ["launches", currentProject] });
      queryClient.invalidateQueries({ queryKey: ["form_codes", currentProject] });
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo Lanzamiento</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormSelect
              form={form}
              name="form_mapping_id"
              label="Formulario JotForm"
              placeholder="Seleccionar formulario"
              options={formMappings?.map(mapping => ({
                id: mapping.id.toString(),
                name: mapping.form_title || mapping.jotform_form_id
              })) || []}
            />

            <FormSelect
              form={form}
              name="id_cliente"
              label="Cliente"
              placeholder="Seleccionar cliente"
              options={clients?.map(client => ({ id: client.id.toString(), name: client.nombre })) || []}
            />

            <FormSelect
              form={form}
              name="template_v2_id"
              label="Plantilla"
              placeholder="Seleccionar plantilla"
              options={templates?.map(template => ({ id: template.id.toString(), name: template.name })) || []}
            />

            <FormSelect
              form={form}
              name="generacion_reporte"
              label="Momento de Generación del Reporte"
              placeholder="Seleccionar momento de generación"
              options={[
                { id: "individual", name: "Individual (tras respuesta individual)" },
                { id: "equipo", name: "Equipo (tras completar equipo)" },
                { id: "individual_equipo", name: "Individual + Equipo (tras completar ambos)" }
              ]}
            />

            <FormSelect
              form={form}
              name="report_type"
              label="Tipo de Reporte"
              placeholder="Seleccionar tipo de reporte"
              options={[
                { id: "individual", name: "Individual (solo variables individuales)" },
                { id: "equipo", name: "Equipo (solo variables de equipo)" },
                { id: "individual_equipo", name: "Individual + Equipo (ambos tipos de variables)" }
              ]}
            />

            <FormSelect
              form={form}
              name="email_sending_type"
              label="Envío de Email"
              placeholder="Seleccionar criterio de envío"
              options={[
                { id: "tras_generacion", name: "Tras generación del reporte" },
                { id: "manual", name: "Manual" }
              ]}
            />

            <NumberInput
              form={form}
              name="max_respuestas"
              label="Máximo de Respuestas por Código"
              placeholder="Ej: 1"
            />

            <NumberInput
              form={form}
              name="report_lifetime"
              label="Tiempo de Vida del Reporte en Días"
              placeholder="Ej: 30"
            />

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creando..." : "Crear Lanzamiento"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
